import { useMutation } from "@apollo/client";
import type { DocumentNode } from "@apollo/client";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { ARCHIVE_GOVERNANCE_ROLE_MUTATION, UNARCHIVE_GOVERNANCE_ROLE_MUTATION } from "./sectionGovernanceMutations";

interface UseSectionGovernanceRoleArchivalResult {
  archiveRole: (id: string) => Promise<boolean>;
  unarchiveRole: (id: string) => Promise<boolean>;
  archiving: boolean;
  unarchiving: boolean;
}

export function useSectionGovernanceRoleArchival(refetchQuery: DocumentNode): UseSectionGovernanceRoleArchivalResult {
  const [archiveGovernanceRole, { loading: archiving }] = useMutation(ARCHIVE_GOVERNANCE_ROLE_MUTATION, {
    refetchQueries: [refetchQuery]
  });
  const [unarchiveGovernanceRole, { loading: unarchiving }] = useMutation(UNARCHIVE_GOVERNANCE_ROLE_MUTATION, {
    refetchQueries: [refetchQuery]
  });

  const archiveRole = async (id: string): Promise<boolean> => {
    try {
      const { data } = await archiveGovernanceRole({ variables: { id } });

      if (!data?.archiveGovernanceRole?.success) {
        showErrorSnackbar(data?.archiveGovernanceRole?.message || "Unable to archive governance role.");
        return false;
      }

      showSnackbar("Governance role archived");
      return true;
    } catch {
      showErrorSnackbar("Unable to archive governance role.");
      return false;
    }
  };

  const unarchiveRole = async (id: string): Promise<boolean> => {
    try {
      const { data } = await unarchiveGovernanceRole({ variables: { id } });

      if (!data?.unarchiveGovernanceRole?.success) {
        showErrorSnackbar(data?.unarchiveGovernanceRole?.message || "Unable to unarchive governance role.");
        return false;
      }

      showSnackbar("Governance role unarchived");
      return true;
    } catch {
      showErrorSnackbar("Unable to unarchive governance role.");
      return false;
    }
  };

  return { archiveRole, unarchiveRole, archiving, unarchiving };
}
